import React, { useEffect, useRef, useState } from "react";

export const DictationWaveForm = ({
  isListening,
  numberOfBars = 65,
}) => {
  const [bars, setBars] = useState(() => Array(numberOfBars).fill(0.1));

  const volumeHistory = useRef(Array(numberOfBars).fill(0));
  const rafRef = useRef(null);
  const streamRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);

  useEffect(() => {
    volumeHistory.current = Array(numberOfBars).fill(0);
    setBars(Array(numberOfBars).fill(0.1));
  }, [numberOfBars]);

  useEffect(() => {
    if (!isListening) {
      volumeHistory.current.fill(0);
      setBars(Array(numberOfBars).fill(0.1));
      return;
    }

    let isMounted = true;

    const startAnalyser = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });

        if (!isMounted) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = stream;

        const AudioContext =
          window.AudioContext || window.webkitAudioContext;

        const audioContext = new AudioContext();
        const analyser = audioContext.createAnalyser();
        const source = audioContext.createMediaStreamSource(stream);

        analyser.fftSize = 256;
        source.connect(analyser);

        audioContextRef.current = audioContext;
        analyserRef.current = analyser;

        const dataArray = new Uint8Array(analyser.frequencyBinCount);

        const tick = () => {
          analyser.getByteTimeDomainData(dataArray);

          let sum = 0;

          for (let i = 0; i < dataArray.length; i += 1) {
            const normalized = (dataArray[i] - 128) / 128;
            sum += normalized * normalized;
          }

          const rms = Math.sqrt(sum / dataArray.length);
          const boosted = Math.min(1, rms * 7);
          const vScale = 0.1 + boosted * 0.9;

          volumeHistory.current.shift();
          volumeHistory.current.push(vScale);

          setBars(
            volumeHistory.current.map((value) => {
              const organic = value * (0.8 + Math.random() * 0.4);
              return Math.min(1, Math.max(0.1, organic));
            }),
          );

          rafRef.current = requestAnimationFrame(tick);
        };

        tick();
      } catch (error) {
        console.log("Microphone analyser error:", error);
        setBars(Array(numberOfBars).fill(0.1));
      }
    };

    startAnalyser();

    return () => {
      isMounted = false;

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }

      if (audioContextRef.current) {
        audioContextRef.current.close();
      }

      rafRef.current = null;
      streamRef.current = null;
      audioContextRef.current = null;
      analyserRef.current = null;
    };
  }, [isListening, numberOfBars]);

  if (!isListening) return null;

  return (
    <div className="flex h-12 w-full items-center justify-start gap-[1.5px] overflow-hidden px-1">
      {bars.map((scale, index) => (
        <div
          key={index}
          className="h-9 flex-1 max-w-[3.5px] origin-center rounded-sm opacity-80 transition-transform duration-75 bg-darkgray"
          style={{
            transform: `scaleY(${scale})`,
          }}
        />
      ))}
    </div>
  );
};
